import React from "react";
import { Droplet, Printer, Waves, Sparkles } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";

const ProductPortfolio = () => {
  const products = [
    {
      icon: Sparkles,
      title: "UV/LED Curable Coatings",
      description: "Our flagship product line featuring instant-cure coatings for all major printing processes.",
      features: [
        "Offset UV/LED Coatings",
        "Screen UV/LED Coatings",
        "Gravure UV/LED Coatings",
        "Flexo UV/LED Coatings",
        "Custom Formulations"
      ],
      highlight: true
    },
    {
      icon: Droplet,
      title: "Gravure Inks",
      description: "High-quality gravure printing inks designed for packaging and publication applications.",
      features: [
        "Solvent-Based Inks",
        "Water-Based Options",
        "High-Speed Performance",
        "Excellent Color Strength",
        "Wide Substrate Range"
      ]
    },
    {
      icon: Printer,
      title: "Security UV/LED Coatings",
      description: "Advanced security coatings with specialized visual effects for anti-counterfeiting and brand protection.",
      features: [
        "Thermochromic Coating",
        "Photochromic Coating",
        "Glow in Dark Coating",
        "Invisible Coating",
        "Tamper-Evident Solutions"
      ]
    },
    {
      icon: Waves,
      title: "Water-Based Emulsions",
      description: "Eco-friendly water-based coatings and emulsions for sustainable printing solutions.",
      features: [
        "Low VOC Emissions",
        "Food-Safe Options",
        "Excellent Adhesion",
        "Cost-Effective",
        "Environmentally Friendly"
      ]
    },
    {
      icon: Droplet,
      title: "Flexo Inks",
      description: "Specialized flexographic inks for flexible packaging and corrugated applications.",
      features: [
        "UV Flexo Inks",
        "High-Speed Compatible",
        "Narrow Web Solutions",
        "Excellent Print Quality",
        "Food-Safe Options"
      ]
    },
    {
      icon: Sparkles,
      title: "Specialty Coatings",
      description: "Custom specialty coatings for unique applications and specific customer requirements.",
      features: [
        "Matte Coatings",
        "Soft-Touch Coatings",
        "Anti-Scratch",
        "Anti-Fingerprint",
        "Custom Development"
      ]
    }
  ];

  return (
    <section id="products" className="section-padding relative overflow-hidden reveal">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1712823600760-1ee072f9ef54?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxjb2F0aW5nJTIwbWFudWZhY3R1cmluZ3xlbnwwfHx8fDE3NjE1OTI0MDh8MA&ixlib=rb-4.1.0&q=85"
          alt="Manufacturing Facility"
          className="w-full h-full object-cover opacity-15"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50/95 via-white/95 to-red-50/95"></div>
      </div>

      <div className="container-custom relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-[#8B1538] font-semibold text-sm uppercase tracking-wider">Complete Range</span>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mt-4 mb-6">
            Product Portfolio
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed">
            Comprehensive selection of printing inks, coatings, and specialty products engineered for excellence.
          </p>
        </div>

        {/* Products Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {products.map((product, index) => (
            <Card
              key={index}
              className={`border-none shadow-lg hover:shadow-2xl transition-all duration-300 ${
                product.highlight ? 'ring-2 ring-[#8B1538] ring-offset-2' : ''
              }`}
            >
              <CardHeader>
                <div className={`${
                  product.highlight ? 'bg-gradient-to-br from-[#8B1538] to-[#DC143C]' : 'bg-red-100'
                } w-14 h-14 rounded-xl flex items-center justify-center mb-4`}>
                  <product.icon className={`h-7 w-7 ${
                    product.highlight ? 'text-white' : 'text-[#8B1538]'
                  }`} />
                </div>
                <CardTitle className="text-xl font-bold text-gray-900">
                  {product.title}
                  {product.highlight && (
                    <span className="ml-2 text-xs bg-[#8B1538] text-white px-2 py-1 rounded-full">Core</span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">{product.description}</p>
                <ul className="space-y-2">
                  {product.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-sm text-gray-700">
                      <div className="w-1.5 h-1.5 bg-[#8B1538] rounded-full mr-2"></div>
                      {feature}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* CTA Banner */}
        <div className="mt-16 text-center">
          <div className="inline-block bg-white rounded-2xl shadow-xl p-8 lg:p-12 border border-gray-100">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">Need a Custom Solution?</h3>
            <p className="text-lg text-gray-600 mb-6 max-w-2xl">
              Our R&D team specializes in developing tailored formulations to meet your specific requirements. Contact us to discuss your unique application needs.
            </p>
            <button
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
              className="bg-[#8B1538] hover:bg-[#DC143C] text-white px-8 py-4 rounded-md font-semibold text-lg transition-colors"
            >
              Request Custom Development
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductPortfolio;
